import React from 'react';

// Hidden divs used to check if the text fits in the box
const sharedStyle = {
  boxSizing: 'content-box',
  textAlign: 'left',
  lineHeight: 1,
  position: 'fixed',
  left: -9999,
  top: -9999,
  visibility: 'hidden',
};
const singlelineStyle = {
  ...sharedStyle,
  overflow: 'visible',
  whiteSpace: 'nowrap',
};
const multilineStyle = {
  ...sharedStyle,
  overflow: 'hidden',
  boxSizing: 'content-box',
  wordBreak: 'break-word',
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const singlelineDiv = () => {
  const div = document.createElement('div');
  for (const key in singlelineStyle) {
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    div.style[key] = singlelineStyle[key];
  }
  document.body.appendChild(div);
  return div;
};
// The multi-line div is being recreated every time the font size
// is calculated to prevent Text blocks from having an incorrect font size.
// At the moment, there is no need for optimization. If the current solution
// proves effective and the errors cease to occur, it may be necessary to consider making some changes.
// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const multiLineDiv = () => {
  const div = document.createElement('div');
  for (const key in multilineStyle) {
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    div.style[key] = multilineStyle[key];
  }
  document.body.appendChild(div);
  return div;
};

// Functions for calculating font sizes
// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const singlelineShrink = (fontSize: number, height: number, width: number, div: HTMLDivElement): number => {
  let newFontSize = Math.min(fontSize, height) - 1;
  while (newFontSize > 0) {
    div.style.fontSize = `${newFontSize}px`;
    if (div.clientWidth < width && div.clientHeight < height) {
      return newFontSize;
    }
    newFontSize--;
  }
  return 0;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const singlelineGrow = (fontSize: number, height: number, width: number, div: HTMLDivElement): number => {
  let newFontSize = fontSize + 1;
  while (newFontSize < 72) {
    div.style.fontSize = `${newFontSize}px`;
    if (div.clientWidth < width && div.clientHeight < height && newFontSize < height) {
      newFontSize++;
    } else {
      return newFontSize - 2;
    }
  }
  return 25;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const multilineShrink = (fontSize: number, height: number, div: HTMLDivElement): number => {
  let newFontSize = Math.min(fontSize, height) - 1;
  while (newFontSize > 0) {
    div.style.fontSize = `${newFontSize}px`;
    if (div.clientHeight < height) {
      return newFontSize;
    }
    newFontSize--;
  }
  return 0;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const multilineGrow = (fontSize: number, height: number, div: HTMLDivElement): number => {
  const maxFontSize = Math.min(height - 1, 25);
  let newFontSize = Math.min(fontSize + 1, maxFontSize);
  while (newFontSize <= maxFontSize) {
    div.style.fontSize = `${newFontSize}px`;
    if (div.clientHeight < height) {
      newFontSize++;
    } else {
      return newFontSize - 1;
    }
  }
  return maxFontSize;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const calculateFontSize = (
  value: string,
  fontSize: number,
  height: number,
  width: number,
  fontFamily: string,
  multiline: boolean
): number => {
  const div = multiline ? multiLineDiv() : singlelineDiv();
  div.textContent = value;
  div.style.fontFamily = fontFamily;
  div.style.whiteSpace = 'break-spaces';
  div.style.fontSize = `${fontSize}px`;
  if (multiline) {
    div.style.width = `${width - 8}px`;
    const newFontSize =
      div.clientHeight >= height || fontSize >= height
        ? multilineShrink(fontSize, height, div)
        : multilineGrow(fontSize, height, div);
    document.body.removeChild(div);
    return newFontSize;
  }

  const newFontSize =
    div.clientWidth > width || div.clientHeight > height || fontSize >= height
      ? singlelineShrink(fontSize, height, width, div)
      : singlelineGrow(fontSize, height, width, div);
  document.body.removeChild(div);
  return newFontSize;
};

// Hook
interface IHookProps {
  height: number;
  width: number;
  initialFontSize?: number;
  initialValue?: string;
  fontFamily?: string;
  multiline?: boolean;
  blockType: string;
}

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const useAutomaticFontSizing = ({
  height,
  width,
  initialFontSize,
  initialValue,
  fontFamily = 'courier',
  multiline = false,
  blockType,
}: IHookProps) => {
  // Set the initial font size to always be less than the height of the block
  const defaultFontSize = height <= 24 ? height - 1 : 24;
  if (initialFontSize && initialFontSize >= height) initialFontSize = height - 1;
  const [fontSize, setFontSize] = React.useState(initialFontSize || defaultFontSize);
  const [value, setValue] = React.useState(initialValue || '');
  const blockTypes = ['Signature', 'Initials', 'DateSigned', 'TimeSigned'];

  const defaultAverageFontSize = 12;

  React.useEffect(() => {
    let newFontSize = calculateFontSize(value, fontSize, height, width, fontFamily, multiline);

    // Give a value to fontsize if 0 or undefined
    if (blockTypes.includes(blockType) && (!newFontSize || newFontSize < 1)) {
      const fontSize =
        defaultAverageFontSize >= height
          ? blockType === 'TimeSigned'
            ? height / 2 + 1
            : height - 1
          : defaultAverageFontSize;
      newFontSize = fontSize;
    }

    setFontSize(newFontSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height, width, value]);

  return { fontSize, value, setValue };
};

export { calculateFontSize, useAutomaticFontSizing };
